import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ClockIcon, ZapIcon, StarIcon } from 'lucide-react';
type PlanOption = 'megakit' | 'fullpack';
export function PricingSection() {
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59
  });
  const [selected, setSelected] = useState<PlanOption>('fullpack');
  const [expandedBump, setExpandedBump] = useState<number | null>(null);
  useEffect(() => {
    let endTime = localStorage.getItem('megaKitOfferEndTime');
    if (!endTime) {
      const newEndTime = new Date().getTime() + 24 * 60 * 60 * 1000;
      localStorage.setItem('megaKitOfferEndTime', newEndTime.toString());
      endTime = newEndTime.toString();
    }
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const distance = parseInt(endTime!) - now;
      if (distance < 0) {
        const newEndTime = new Date().getTime() + 24 * 60 * 60 * 1000;
        localStorage.setItem('megaKitOfferEndTime', newEndTime.toString());
      } else {
        setTimeLeft({
          hours: Math.floor(distance % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
          minutes: Math.floor(distance % (1000 * 60 * 60) / (1000 * 60)),
          seconds: Math.floor(distance % (1000 * 60) / 1000)
        });
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const formatTime = (time: number) => time.toString().padStart(2, '0');
  const plans = {
    megakit: {
      name: 'MEGA KIT',
      emoji: '📘',
      desc: '6 Tomos + 150 Fichas + 7 Bonos',
      oldPrice: '$47.920',
      price: '$17.990',
      link: 'https://alfacheck.ebookditales.com/cart/46281401598091:1',
      ctaText: 'SÍ, QUIERO EL MEGA KIT →'
    },
    fullpack: {
      name: 'MEGA PACK FULL',
      emoji: '🏆',
      desc: 'Todo el MEGA KIT + 3 Complementos exclusivos',
      oldPrice: '$56.220',
      price: '$24.990',
      link: 'https://alfacheck.ebookditales.com/cart/46281410281611:1',
      ctaText: 'SÍ, QUIERO EL MEGA PACK FULL →',
      badge: 'MÁS ELEGIDO'
    }
  };
  const megakitIncludes = ['6 tomos de guía paso a paso', '150 fichas listas para imprimir', 'Seguimiento integrado por tomo', '7 bonos estratégicos completos', 'Instrucciones claras por ficha', 'De vocales a escritura autónoma'];
  const orderBumps = [{
    id: 1,
    title: 'El Traductor de Berrinches',
    price: '$3.900',
    hook: '¿Tu hijo llora y no sabés si es berrinche o crisis?',
    desc: 'Sistema paso a paso para leer señales ANTES de que explote y saber qué hacer en crisis.',
    bullets: ['Tabla berrinche vs crisis de un vistazo', 'Semáforo Sensorial: verde, amarillo, rojo', 'Diccionario de 12 conductas frecuentes', 'Protocolo de 60 Segundos para crisis', '5 técnicas de regulación sensorial', 'Infografía imprimible del Protocolo']
  }, {
    id: 2,
    title: 'Recetario 30 Juegos Sensoriales',
    price: '$2.500',
    hook: '¿Sabías que tu cocina es el mejor equipamiento sensorial?',
    desc: 'Transformá harina, sal y almohadones en herramientas terapéuticas para el aprendizaje.',
    bullets: ['30 actividades por nivel de energía', '10 juegos de cocina sensoriales', '10 juegos de presión profunda', '5 botellas sensoriales caseras', '5 circuitos de motricidad gruesa', 'Conexión con fichas del MEGA KIT']
  }, {
    id: 3,
    title: 'SOS Escuela: Carpeta Docente',
    price: '$1.900',
    hook: '¿Cansada de explicar todo cada vez que hay maestra nueva?',
    desc: 'La carpeta que entregás el primer día y responde todas las preguntas.',
    bullets: ['Carta de presentación lista', 'Ficha personal del niño', 'Hoja de primeros auxilios docente', '5 frases clave sin generar crisis', '10 adaptaciones rápidas', 'Registro semanal escuela-familia']
  }];
  const currentPlan = plans[selected];
  return <section id="compra" className="min-h-[100svh] flex flex-col justify-center py-5 md:py-20 px-4 bg-[#7B5EA7] text-white relative overflow-hidden scroll-mt-10" data-id="element-135">
      <div className="max-w-2xl mx-auto relative z-10 w-full" data-id="element-136">
        {/* Timer + heading */}
        <div className="text-center mb-4 md:mb-10" data-id="element-137">
          <div className="inline-flex items-center justify-center gap-2 bg-white/20 text-white border border-white/30 px-3 py-1.5 md:px-6 md:py-3 rounded-full mb-3 md:mb-8" data-id="element-138">
            <ClockIcon className="w-3.5 h-3.5 md:w-5 md:h-5 animate-pulse" data-id="element-139" />
            <span className="font-bold tracking-wider text-[0.65rem] md:text-base" data-id="element-140">
              ⏰ EXPIRA EN: {formatTime(timeLeft.hours)}:
              {formatTime(timeLeft.minutes)}:{formatTime(timeLeft.seconds)}
            </span>
          </div>

          <h2 className="text-2xl md:text-5xl font-black mb-1 md:mb-3" data-id="element-141">
            Elegí tu <span className="text-white" data-id="element-142">MEGA KIT:</span>
          </h2>
          <p className="text-white/70 text-xs md:text-lg" data-id="element-143">
            Todas las opciones incluyen los 7 bonos estratégicos
          </p>
        </div>

        {/* Radio cards */}
        <div className="space-y-2.5 md:space-y-4 mb-4 md:mb-8" data-id="element-144">
          {/* Option 1: MEGA KIT */}
          <button onClick={() => setSelected('megakit')} className={`w-full text-left rounded-xl md:rounded-2xl p-3 md:p-6 flex items-center gap-3 md:gap-4 transition-all duration-300 ${selected === 'megakit' ? 'bg-white/15 border-2 border-white shadow-[0_0_20px_rgba(255,255,255,0.15)]' : 'bg-white/5 border-2 border-white/20'}`} data-id="element-145">
            <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selected === 'megakit' ? 'border-white' : 'border-white/40'}`} data-id="element-146">
              {selected === 'megakit' && <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-white" data-id="element-147"></div>}
            </div>

            <div className="flex-grow min-w-0" data-id="element-148">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-149">
                <span className="text-base md:text-xl" data-id="element-150">
                  {plans.megakit.emoji}
                </span>
                <h3 className="text-sm md:text-xl font-black text-white" data-id="element-151">
                  {plans.megakit.name}
                </h3>
              </div>
              <p className="text-[0.65rem] md:text-sm text-white/60 leading-tight" data-id="element-152">
                {plans.megakit.desc}
              </p>
            </div>

            <div className="text-right flex-shrink-0" data-id="element-153">
              <div className="text-[0.6rem] md:text-sm text-white/50 line-through" data-id="element-154">
                {plans.megakit.oldPrice}
              </div>
              <div className={`text-lg md:text-3xl font-black ${selected === 'megakit' ? 'text-white' : 'text-white/80'}`} data-id="element-155">
                {plans.megakit.price}
              </div>
            </div>
          </button>

          {/* Option 2: MEGA PACK FULL */}
          <button onClick={() => setSelected('fullpack')} className={`w-full text-left rounded-xl md:rounded-2xl p-3 md:p-6 flex items-center gap-3 md:gap-4 transition-all duration-300 relative ${selected === 'fullpack' ? 'bg-white/15 border-2 border-white shadow-[0_0_20px_rgba(255,255,255,0.15)]' : 'bg-white/5 border-2 border-white/20'}`} data-id="element-156">
            <div className={`w-5 h-5 md:w-6 md:h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selected === 'fullpack' ? 'border-white' : 'border-white/40'}`} data-id="element-157">
              {selected === 'fullpack' && <div className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-white" data-id="element-158"></div>}
            </div>

            <div className="flex-grow min-w-0" data-id="element-159">
              <div className="flex items-center gap-1.5 mb-0.5" data-id="element-160">
                <span className="text-base md:text-xl" data-id="element-161">
                  {plans.fullpack.emoji}
                </span>
                <h3 className="text-sm md:text-xl font-black text-white" data-id="element-162">
                  {plans.fullpack.name}
                </h3>
              </div>
              <div className="inline-flex items-center gap-1 bg-white text-[#111111] text-[0.55rem] md:text-xs font-bold px-1.5 py-0.5 md:px-2.5 rounded mb-0.5" data-id="element-163">
                <StarIcon className="w-2.5 h-2.5 md:w-3 md:h-3 fill-[#111111]" data-id="element-164" />{' '}
                MÁS ELEGIDO
              </div>
              <p className="text-[0.65rem] md:text-sm text-white/60 leading-tight" data-id="element-165">
                {plans.fullpack.desc}
              </p>
            </div>

            <div className="text-right flex-shrink-0" data-id="element-166">
              <div className="text-[0.6rem] md:text-sm text-white/50 line-through" data-id="element-167">
                {plans.fullpack.oldPrice}
              </div>
              <div className={`text-lg md:text-3xl font-black ${selected === 'fullpack' ? 'text-white' : 'text-white/80'}`} data-id="element-168">
                {plans.fullpack.price}
              </div>
            </div>
          </button>
        </div>

        {/* CTA Button */}
        <a href={currentPlan.link} className="block w-full bg-[#22C55E] hover:bg-[#16a34a] text-white text-center font-black text-base md:text-xl py-3.5 md:py-5 px-4 rounded-full shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300 relative overflow-hidden group mb-2 md:mb-4" data-id="element-169">
          <div className="absolute inset-0 -translate-x-full group-hover:animate-[shimmer_1.5s_infinite] bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" data-id="element-170"></div>
          <span className="relative z-10" data-id="element-171">{currentPlan.ctaText}</span>
        </a>

        <p className="text-center text-white/50 text-[0.65rem] md:text-sm italic mb-3 md:mb-6" data-id="element-172">
          Podés cambiar de opción cuando quieras
        </p>

        {/* Trust badges */}
        <div className="flex justify-center gap-3 md:gap-4 mb-4 md:mb-16 text-[0.6rem] md:text-sm font-medium text-white/70" data-id="element-173">
          <span className="flex items-center gap-0.5" data-id="element-174">
            <CheckIcon className="w-3 h-3 md:w-4 md:h-4 text-white" data-id="element-175" /> Pago
            único
          </span>
          <span className="flex items-center gap-0.5" data-id="element-176">
            <CheckIcon className="w-3 h-3 md:w-4 md:h-4 text-white" data-id="element-177" /> Acceso
            inmediato
          </span>
          <span className="flex items-center gap-0.5" data-id="element-178">
            <CheckIcon className="w-3 h-3 md:w-4 md:h-4 text-white" data-id="element-179" /> Descarga
            ilimitada
          </span>
        </div>

        {/* Dynamic includes */}
        <AnimatePresence mode="wait" data-id="element-180">
          <motion.div key={selected} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.2
        }} className="bg-white/10 rounded-2xl md:rounded-3xl p-4 md:p-8 border border-white/20 max-h-[28vh] md:max-h-none overflow-y-auto" data-id="element-181">
            <h3 className="text-sm md:text-2xl font-bold mb-3 md:mb-6 text-center" data-id="element-182">
              {selected === 'megakit' ? 'EL MEGA KIT INCLUYE' : 'EL MEGA PACK FULL INCLUYE'}
            </h3>

            <ul className="grid grid-cols-2 md:grid-cols-2 gap-x-3 gap-y-1.5 md:gap-3 mb-1" data-id="element-183">
              {megakitIncludes.map((item, idx) => <li key={idx} className="flex items-start gap-1.5 md:gap-2.5" data-id="element-184">
                  <CheckIcon className="w-3 h-3 md:w-5 md:h-5 text-white flex-shrink-0 mt-0.5" data-id="element-185" />
                  <span className="text-white/80 text-[0.6rem] md:text-sm leading-tight" data-id="element-186">
                    {item}
                  </span>
                </li>)}
            </ul>

            {selected === 'fullpack' && <div className="mt-3 pt-3 border-t border-white/20" data-id="element-187">
                <div className="flex items-center gap-1.5 mb-2.5 justify-center" data-id="element-188">
                  <ZapIcon className="w-3 h-3 md:w-4 md:h-4 text-white" data-id="element-189" />
                  <span className="text-[0.6rem] md:text-sm font-bold text-white uppercase tracking-wider" data-id="element-190">
                    + 3 Complementos exclusivos
                  </span>
                </div>

                <div className="space-y-1.5 md:space-y-3" data-id="element-191">
                  {orderBumps.map(bump => <div key={bump.id} className="bg-white/5 rounded-lg md:rounded-xl overflow-hidden border border-white/15" data-id="element-192">
                      <button onClick={() => setExpandedBump(expandedBump === bump.id ? null : bump.id)} className="w-full px-3 py-2 md:px-4 md:py-3 flex items-center justify-between text-left" data-id="element-193">
                        <div className="flex items-center gap-1.5 md:gap-2.5 flex-grow pr-2" data-id="element-194">
                          <ZapIcon className="w-3 h-3 md:w-4 md:h-4 text-white flex-shrink-0" data-id="element-195" />
                          <span className="text-[0.65rem] md:text-sm font-bold text-white leading-tight" data-id="element-196">
                            {bump.title}
                          </span>
                          <span className="text-[0.55rem] md:text-xs text-white/50 flex-shrink-0" data-id="element-197">
                            {bump.price}
                          </span>
                        </div>
                        <svg className={`w-3 h-3 md:w-4 md:h-4 text-white/50 flex-shrink-0 transition-transform duration-200 ${expandedBump === bump.id ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-198">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-199" />
                        </svg>
                      </button>

                      <AnimatePresence data-id="element-200">
                        {expandedBump === bump.id && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.2
                  }} className="overflow-hidden" data-id="element-201">
                            <div className="px-3 pb-2.5 md:px-4 md:pb-4 border-t border-white/10" data-id="element-202">
                              <p className="text-[0.6rem] md:text-sm text-white font-medium mt-2 mb-1.5 italic leading-tight" data-id="element-203">
                                {bump.hook}
                              </p>
                              <p className="text-[0.55rem] md:text-xs text-white/60 mb-2 leading-relaxed" data-id="element-204">
                                {bump.desc}
                              </p>
                              <ul className="grid grid-cols-2 gap-x-2 gap-y-1" data-id="element-205">
                                {bump.bullets.map((b, i) => <li key={i} className="flex items-start gap-1" data-id="element-206">
                                    <CheckIcon className="w-2.5 h-2.5 md:w-3 md:h-3 text-white flex-shrink-0 mt-0.5" data-id="element-207" />
                                    <span className="text-[0.55rem] md:text-xs text-white/60 leading-tight" data-id="element-208">
                                      {b}
                                    </span>
                                  </li>)}
                              </ul>
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>)}
                </div>
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}